import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguagesFetchAPI } from '../../../api/Languages';
import { AuthFetchAPI } from '../../../api/Auth';
import LoadingAlert from '../../Alerts/LoadingAlert';
import { changeLanguage as setAppLanguage, getCurrentLanguage } from '../../../i18n';

// /languages requires a valid token (any type - verifyToken just checks the JWT signs, see
// smartpay-dashboard verifyToken middleware). This component renders on every page before any
// client login happens, so the only token available here is the business-scoped public one -
// generatePublicToken's cookie is cross-origin (frontend and API are different hosts) and gets
// blocked by browsers that refuse third-party cookies (always the case in Incognito/InPrivate),
// so grab the token from its response body and send it explicitly instead of relying on the cookie.
const getPublicToken = async () => {
  try {
    const business = JSON.parse(localStorage.getItem('business') || 'null');
    if (!business?.id) return undefined;

    const valid = await AuthFetchAPI.validToken();
    if (valid?.data === true) return undefined; // existing cookie already works, nothing to add

    const response = await AuthFetchAPI.generatePublicToken(business.id);
    return response?.data?.token || undefined;
  } catch (err) {
    return undefined;
  }
};

const Ico = {
  Globe: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  )
};

/**
 * Icono de globo + dropdown de idiomas, pensado para vivir en el "dn-right" del
 * navbar nuevo (HeaderMain.jsx). Reutiliza las clases dn-icon/dn-lang-wrap/dn-lang-drop/dn-lang-item
 * que ya existen en assets/scss/pages/_navbar.scss (estaban definidas pero sin usar).
 */
export default function LanguageSwitcher() {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const activeLang = getCurrentLanguage();

  useEffect(() => {
    let mounted = true;
    getPublicToken()
      .then((token) => LanguagesFetchAPI.getLanguages(token))
      .then((res) => {
        if (mounted) setLanguages(res?.data?.languages || []);
      })
      .catch(() => {
        // silencioso: si no hay idiomas configurados simplemente no mostramos el selector
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleSelect = (code) => {
    setOpen(false);
    if (code === activeLang) return;

    LoadingAlert(t('language.changing'), t('language.changingSubtitle'));
    setAppLanguage(code);

    // Recargamos para que las vistas ya cargadas (productos, categorias, segmentos,
    // promociones) vuelvan a pedirse al backend en el nuevo idioma
    setTimeout(() => {
      window.location.reload();
    }, 1200);
  };

  if (!languages || languages.length === 0) return null;

  return (
    <div className="dn-lang-wrap" ref={wrapRef}>
      <button type="button" className="dn-icon" onClick={() => setOpen((v) => !v)} title={t('language.label')}>
        <Ico.Globe />
      </button>
      {open && (
        <div className="dn-lang-drop">
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              className={`dn-lang-item${activeLang === language.code ? ' active' : ''}`}
              onClick={() => handleSelect(language.code)}
            >
              {language.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
